import { ref, computed, toRaw } from "vue"
import { useStore } from "vuex"

import { useWidgetAddToCart } from '@/components/widgets/composables/useWidgetAddToCart'

import type {
  PartRow
} from '@/interfaces/library/partCollection'


export default function useCart() {
  const store = useStore()
  const { emitWidgetAddToCartEvent } = useWidgetAddToCart()

  const addToCartModalOpen = ref(false)
  const cartItem = ref<PartRow|null>(null)

  const isWidget = computed(() => {
    return store.state.auth.isWidget
  })
  const isWidgetCartEnabled = computed(() => {
    return store.getters['widgets/isWidgetCartEnabled']
  })
  const isCartOrErpCartEnabled = computed(() => {
    return store.getters['user/isCartOrErpCartEnabled']
  })
  const hasCartPromptForQtyEnabled = computed(() => {
    return store.getters['user/hasCartPromptForQtyEnabled']
  })
  const isCartLoaded = computed(() => {
    return store.state.cart.isLoaded
  })
  const addToCartQtyBehavior = computed(() => {
    return store.state.user.preferences.addToCartQtyBehavior
  })

  function canOrderPart(part: PartRow) {
    if (!part.partNumber || !part.orderable) return false
    if (isWidget.value) return isWidgetCartEnabled.value
    return isCartOrErpCartEnabled.value
  }
  function closeAddToCartModal() {
    addToCartModalOpen.value = false
  }

  function addToCart(part: PartRow, getPartLocation: Function) {
    if (!isWidgetCartEnabled.value && !isCartLoaded.value) return

    if (hasCartPromptForQtyEnabled.value) {
      cartItem.value = part
      addToCartModalOpen.value = true
    } else {
      if (isWidgetCartEnabled.value) {
        emitWidgetAddToCartEvent({
          content: part,
          qty: null,
          hasCartPromptForQtyEnabled: hasCartPromptForQtyEnabled.value,
          addToCartQtyBehavior: addToCartQtyBehavior.value
        })
      } else {
        const params = {
          part: toRaw(part),
          qty: getCartQuantity(null, part),
          partLocation: getPartLocation(part)
        }
        store.dispatch('cart/addToCart', params)
      }
    }
  }

  function addPartsToCart(parts: PartRow[], getPartLocation: Function) {
    parts.forEach(part => {
      if (!canOrderPart(part) || !!part.partCode) return

      const params = {
        part: toRaw(part),
        qty: part.quantity || 1,
        partLocation: getPartLocation(part)
      }
      if (isWidgetCartEnabled.value) {
        emitWidgetAddToCartEvent({
          content: params.part,
          qty: params.qty,
          hasCartPromptForQtyEnabled: hasCartPromptForQtyEnabled.value,
          addToCartQtyBehavior: addToCartQtyBehavior.value
        })
      } else {
        store.dispatch('cart/addToCart', params)
      }
    })
  }

  function getCartQuantity(qty: number|null, part: PartRow|null) {
    let quantity = 1
    switch (addToCartQtyBehavior.value) {
      case 'qtyZero':
        quantity = hasCartPromptForQtyEnabled.value ? 0 : 1
        break
      case 'qtyFromBom':
        quantity = parseInt(part?.quantity ?? '', 10) || 1
        break
      default:
        quantity = 1
    }

    return qty || quantity
  }

  function handleCartModalSubmit(qty: number, getPartLocation: Function) {
    addToCartModalOpen.value = false

    const params = {
      part: toRaw(cartItem.value),
      qty: getCartQuantity(qty, cartItem.value),
      partLocation: getPartLocation(cartItem.value)
    }

    if (isWidgetCartEnabled.value) {
      emitWidgetAddToCartEvent({
        content: params.part,
        qty: qty,
        hasCartPromptForQtyEnabled: hasCartPromptForQtyEnabled.value,
        addToCartQtyBehavior: addToCartQtyBehavior.value
      })
    } else {
      store.dispatch('cart/addToCart', params)
    }
  }

  return {
    cartItem,
    addToCartModalOpen,
    isCartOrErpCartEnabled,
    isWidgetCartEnabled,
    addToCart,
    addPartsToCart,
    handleCartModalSubmit,
    canOrderPart,
    closeAddToCartModal
  }
}